import { asType } from '@sparky/framework'
import { ActivationManifest, PrimaryAppIds } from '../renderer'
import { IOT_PACKAGE_1, IOT_PACKAGE_2, DAS_BANNER_MESSAGE, IOT_APP_ID, IOT_3P_ADDON_APP_ID } from '../constants'


const defaultCspDropdown = {
  key: 'default-csp-component',
  component: 'activation.CSPDropDownWrapper',
  props: {
    display: {
      errorMessage: 'Select Customer support Account to proceed activation',
      toolTipMessage: 'Select the Customer support account where the license is to be activated',
      subTitle: 'Customer support account for this tenant'
    },
    dropDownProps: {
      required: true,
      triggerValidationOnChange: true,
    },
    size: {
      height: 32,
      width: 300
    },
    passByProps: {
      dataTestId: 'csp-activation-screen',
      enableSearch: true,
      label: 'Customer Support Account',
      placeholder: 'Select Customer Support Account',
    }
  }
}

const defaultRegionDropDown = {
  key: 'default-region-component',
  component: 'activation.RegionDropDown',
  props: {
    header: true,
    display: {
      errorMessage: 'Select Region to proceed activation',
      toolTipMessage: 'Select the Region where the cdl needs to be provisioned',
      subTitle: 'Deployment region and where your data logs are stored'
    },
    dropDownProps: {
      required: true,
      triggerValidationOnChange: true,
    },
    size: {
      height: 32,
      width: 300
    },
    passByProps: {
      dataTestId: 'region-activation-screen',
      enableSearch: false,
      label: 'Region',
      placeholder: 'Select Region',
    }
  }
}

const defaultTsgDropDownPassByProps = {
  enableSearch: true,
  label: 'Tenant',
  dataMetrics: 'default-tsg-component',
  dataTestId: 'tsg-activation-screen',
  placeholder: 'Select Tenant / Create New',
  menuStyle: { width: 300 }
}
const defaultTsgDropDownProps = {
  create_new: true,
  showAllTSG: true,
  showErrorIfPrimaryAppAddonExists: true,
  display: {
    errorMessage: 'Select TSG to proceed activation',
    toolTipMessage: 'Select the TSG where the license needs to be provisioned',
    subTitle: 'The tenant where the license will be activated'
  },
  dropDownProps: {
    required: true,
    addNewValue: true,
    triggerValidationOnChange: true,
    addNewProps: {
      conditionValue: 'ADD_NEW',
      updatedValue: '',
      createValue: '[Create New]'
    }
  },
  size: {
    height: 32,
    width: 300
  },
  passByProps: defaultTsgDropDownPassByProps
}

const ioT3PTsgDropDownProps = {
  ...defaultTsgDropDownProps,
  create_new: false, // no new TSG option
  passByProps: {
    ...defaultTsgDropDownPassByProps,
    placeholder: 'Select Tenant', // updated place holder text
  }
}

const defaultDropdowns = (tsgDropDownProps: any = defaultTsgDropDownProps) => {
  return {
    key: 'default-dropdown-component',
    component: 'activation.DefaultActivationCommonDropdowns',
    props: {
      group: 1, // used for grouping elemenets/children to apply common properties such as style.
      children: [
        {
          key: 'default-tsg-component',
          component: 'activation.TSGDropDown',
          props: tsgDropDownProps
        },
        defaultCspDropdown,
        defaultRegionDropDown
      ]
    },
  }
}

const defaultDivider = {
  key: 'default-divider-component',
  component: 'activation.DividerLine',
  props: {}
}
const defaultPrimaryLicenseDetails = {
  key: 'default-license-details-component',
  component: 'activation.DefaultPrimaryLicenseDetails',
  props: {
    group: 1,
  }
}
const defaultCdl = {
  key: 'default-cdl-field',
  component: 'activation.DefaultCDLField',
  props: {
    group: 1,
    cdlDropDownProps: {
      display: {
        errorMessage: 'Select SLS to proceed activation',
        subTitle: 'SLS instance for this tenant'
      },
      dropDownProps: {
        required: true,
        triggerValidationOnChange: true,
      },
      size: {
        height: 32,
        width: 355
      },
      passByProps: {
        // disabled: true,
        dataTestId: 'cdl-activation-screen',
        enableSearch: true,
        label: '',
        placeholder: 'Select SLS Instance',
      }
    },
  },
}
const defaultSubDomainField = {
  key: 'default-sub-domain-field',
  component: 'activation.SubDomainField',
  props: {
    group: 1,
  }
}

const defaultAddonField = {
  key: 'default-add-on-field',
  component: 'activation.DefaultAddonField',
  props: {
    group: 1,
  }
}

const defaultDasBanner = {
  key: 'activation-banner',
  component: 'activation.ActivationBanner',
  props: {
    group: 1,
    appearance: 'standard',
    type: 'inline',
    infoMessage: DAS_BANNER_MESSAGE,
  }
}

const defaultInstanceTable = {
  key: 'instance-table',
  component: 'activation.InstanceTable',
  props: {
    group: 2,
    title: 'Active Subscriptions',
    subTitle: ''
  }
}
const defaultFooter = {
  key: 'form-footer',
  component: 'activation.FormFooter',
  props: {
    group: 3,
    buttonText: 'Activate',
    dataTestId: 'activate-button'
  }
}

export default asType<ActivationManifest>({
  activation_id: IOT_APP_ID,
  description: 'IoT Security',
  primary_app_id: PrimaryAppIds.ZINGBOX,
  flags: {
    hash_required: true,
    cdl_required: true, // default set to true and handle code level for 2.0
    sub_domain_required: true,
    has_license_packages: true,
    addons: [{
      app_id: IOT_3P_ADDON_APP_ID,
      display_name: 'IoT Third party addon',
      licenseTypeTitle: 'Plan',
      required_x_signature: true,
      autoActivate: true, //  activation form will automatically adds into 'set' form fields,
      licenseTypeEnums: {
        'IOTBASIC': 'Basic',
        'IOTADV': 'Advanced',
        'IOTADVEVAL': 'Advanced',
        'IOTADVEVAL60': 'Advanced',
        'RENIOTADV': 'Advanced',
        'IOTADVTRIAL': 'Advanced',
      }
    }],
  },
  extra: {
    [IOT_PACKAGE_1]: [
      defaultDivider,
      defaultPrimaryLicenseDetails,
      defaultAddonField,
      defaultDivider,
      defaultCdl,
      defaultDivider,
      defaultSubDomainField,
      defaultDivider,
      defaultDasBanner,
      defaultDivider,
      defaultInstanceTable,
      defaultFooter,
    ],
    [IOT_PACKAGE_2]: [
      defaultDivider,
      defaultPrimaryLicenseDetails,
      defaultAddonField,
      defaultDivider,
      defaultSubDomainField,
      defaultDivider,
      defaultDasBanner,
      defaultDivider,
      defaultInstanceTable,
      defaultFooter,
    ],
    [IOT_3P_ADDON_APP_ID]: [
      defaultDropdowns(ioT3PTsgDropDownProps),
      defaultAddonField,
      defaultInstanceTable,
      defaultFooter,
    ]
  },
  activation_screens: [
    {
      key: 'activate',
      component: 'activation.DefaultActivateScreen',
      props: {
        title: '',
        description: '',
        primaryButton: {
          text: 'Activate Now',
        },
        children: [
          {
            key: 'screen-header',
            component: 'activation.ScreenHeader',
            props: {
              title: 'Activate IoT Security',
              subTitle: ''
            }
          },
          {
            key: 'ela-header',
            component: 'activation.ElaLicenseInfo',
            props: {
            }
          },
          {
            key: 'activation-form',
            component: 'activation.DefaultActivationForm',
            props: {
              title: '',
              subTitle: '',
              children: [
                defaultDropdowns(),
              ],
            }
          },
          {
            key: 'screen-footer',
            component: 'activation.ScreenFooter',
            props: {
            }
          }
        ],
      },
    }
  ],
})
